import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { FileX2 } from 'lucide-react-native';

import { Text } from '@/components/common/AppText';
import { NativeButton } from '../common/NativeButton';
import { useTheme } from '../../hooks/useTheme';
import { getCvAccessErrorCopy } from '../../lib/cv-access-error';

// Issue #256: the CV workspace rendered nothing when the profile fetch failed,
// so a shared CV whose grant no longer resolves looked like a broken app. This
// says what happened and gives a way out.

export function CvAccessErrorState({
  target,
  onRetry,
}: {
  target?: string | null;
  onRetry: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('seeker');
  const router = useRouter();
  const copy = getCvAccessErrorCopy(target);

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <View style={[styles.iconWrap, { backgroundColor: theme.surfaceSecondary }]}>
        <FileX2 size={26} color={theme.textTertiary} strokeWidth={1.8} />
      </View>
      <Text style={[styles.title, { color: theme.text }]}>{t(copy.titleKey)}</Text>
      <Text style={[styles.body, { color: theme.textSecondary }]}>
        {t(copy.bodyKey)}
      </Text>
      <View style={styles.actions}>
        <NativeButton
          title={t(copy.primaryActionKey)}
          onPress={onRetry}
          variant="solid"
          size="md"
          pill
        />
        {copy.secondaryActionKey ? (
          <NativeButton
            title={t(copy.secondaryActionKey)}
            onPress={() => router.replace('/cv' as never)}
            variant="ghost"
            size="md"
            pill
          />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 360,
  },
  actions: {
    marginTop: 8,
    gap: 8,
    alignItems: 'center',
  },
});
